import { useEffect, useState } from "react";
import { useSession } from "@/auth/session";
import {
  createOperationType,
  deleteOperationType,
  listOperationTypes,
  updateOperationType,
  type OperationTypeItem,
} from "@/lib/api";

const CATEGORIES = [
  { value: "reparacion", label: "Reparación" },
  { value: "instalacion", label: "Instalación" },
  { value: "lectura", label: "Lectura" },
  { value: "corte", label: "Corte" },
];

const categoryLabel = (cat: string) => CATEGORIES.find((c) => c.value === cat)?.label ?? cat;

type FormState = { name: string; code: string; category: string };
const emptyForm: FormState = { name: "", code: "", category: "reparacion" };

function toCode(name: string): string {
  return name
    .toLowerCase()
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_|_$/g, "")
    .slice(0, 40);
}

export default function TiposOTTab() {
  const { session } = useSession();
  const [items, setItems] = useState<OperationTypeItem[]>([]);
  const [form, setForm] = useState<FormState>(emptyForm);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    async function load(): Promise<void> {
      if (!session) return;
      setLoading(true);
      setError(null);
      try {
        const rows = await listOperationTypes(session);
        if (!cancelled) setItems(rows);
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : "No se pudieron cargar tipos");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    void load();
    return () => { cancelled = true; };
  }, [session]);

  function openCreate(): void {
    setEditingId(null);
    setForm(emptyForm);
    setError(null);
    setShowModal(true);
  }

  function openEdit(item: OperationTypeItem): void {
    setEditingId(item.id);
    setForm({ name: item.name, code: item.code, category: item.category });
    setError(null);
    setShowModal(true);
  }

  function closeModal(): void {
    setShowModal(false);
    setEditingId(null);
    setForm(emptyForm);
    setError(null);
  }

  function handleNameChange(name: string): void {
    setForm((prev) => ({
      ...prev,
      name,
      code: editingId ? prev.code : toCode(name),
    }));
  }

  async function onSave(): Promise<void> {
    if (!session || !form.name.trim() || !form.code.trim()) return;
    setSaving(true);
    setError(null);
    try {
      if (editingId) {
        const updated = await updateOperationType(session, editingId, { name: form.name.trim(), category: form.category });
        setItems((prev) => prev.map((i) => (i.id === editingId ? updated : i)));
      } else {
        const created = await createOperationType(session, { name: form.name.trim(), code: form.code.trim(), category: form.category });
        setItems((prev) => [...prev, created].sort((a, b) => a.name.localeCompare(b.name, "es")));
      }
      closeModal();
    } catch (e) {
      setError(e instanceof Error ? e.message : "No se pudo guardar");
    } finally {
      setSaving(false);
    }
  }

  async function onDelete(item: OperationTypeItem): Promise<void> {
    if (!session || !window.confirm(`¿Eliminar tipo "${item.name}"?`)) return;
    setError(null);
    try {
      await deleteOperationType(session, item.id);
      setItems((prev) => prev.filter((i) => i.id !== item.id));
    } catch (e) {
      setError(e instanceof Error ? e.message : "No se pudo eliminar");
    }
  }

  return (
    <div className="space-y-5">
      <div className="bg-white rounded-xl border border-gray-100 p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-sm font-semibold text-gray-800">Tipos de orden de trabajo ({items.length})</h2>
          <button
            onClick={openCreate}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700"
          >
            <i className="ri-add-line" />
            Nuevo tipo
          </button>
        </div>

        {loading ? (
          <div className="h-16 bg-gray-100 rounded animate-pulse" />
        ) : (
          <div className="space-y-2">
            {items.length === 0 ? (
              <p className="text-sm text-gray-500">Sin tipos de OT registrados.</p>
            ) : (
              items.map((item) => (
                <div key={item.id} className="flex items-center justify-between border border-gray-100 rounded-lg px-3 py-2">
                  <div>
                    <div className="text-sm font-medium text-gray-800">{item.name}</div>
                    <div className="text-xs text-gray-400">
                      {categoryLabel(item.category)} · <span className="font-mono">{item.code}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button onClick={() => openEdit(item)} className="text-xs px-2 py-1 border border-gray-200 rounded hover:bg-gray-50">
                      Editar
                    </button>
                    <button
                      onClick={() => void onDelete(item)}
                      className="text-xs px-2 py-1 border border-red-200 text-red-600 rounded hover:bg-red-50"
                    >
                      Eliminar
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        )}

        {error && <p className="mt-3 text-xs text-red-600">{error}</p>}
      </div>

      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30">
          <div className="bg-white rounded-xl border border-gray-100 w-full max-w-sm p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-gray-800">{editingId ? "Editar tipo de OT" : "Nuevo tipo de OT"}</h3>
              <button onClick={closeModal} className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-gray-100 text-gray-400">
                <i className="ri-close-line" />
              </button>
            </div>

            <div className="space-y-3">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Nombre</label>
                <input
                  value={form.name}
                  onChange={(e) => handleNameChange(e.target.value)}
                  placeholder="Ej: Reparación fuga domiciliaria"
                  className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-blue-400"
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Código</label>
                <input
                  value={form.code}
                  onChange={(e) => setForm((prev) => ({ ...prev, code: e.target.value }))}
                  placeholder="reparacion_fuga_domiciliaria"
                  disabled={!!editingId}
                  className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg font-mono text-xs disabled:bg-gray-50 disabled:text-gray-400 focus:outline-none focus:border-blue-400"
                />
                {!editingId && (
                  <p className="text-xs text-gray-400 mt-1">Se genera automáticamente desde el nombre. Único e inmutable.</p>
                )}
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Categoría</label>
                <select
                  value={form.category}
                  onChange={(e) => setForm((prev) => ({ ...prev, category: e.target.value }))}
                  className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-blue-400"
                >
                  {CATEGORIES.map((c) => (
                    <option key={c.value} value={c.value}>{c.label}</option>
                  ))}
                </select>
              </div>

              {error && <p className="text-xs text-red-600">{error}</p>}
            </div>

            <div className="flex gap-3 mt-5">
              <button onClick={closeModal} className="flex-1 px-4 py-2 text-sm border border-gray-200 rounded-lg hover:bg-gray-50">
                Cancelar
              </button>
              <button
                onClick={() => void onSave()}
                disabled={saving || !form.name.trim() || !form.code.trim()}
                className="flex-1 px-4 py-2 text-sm bg-blue-600 text-white rounded-lg disabled:opacity-60 hover:bg-blue-700"
              >
                {saving ? "Guardando..." : editingId ? "Actualizar" : "Crear tipo"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
